import {useState, useEffect, useRef, Fragment} from 'react';
import {ContentHeader, DatePicker, Loader} from '@app/components';
import {Formik, Field, Form} from 'formik';
import {useParams, useNavigate, Link} from 'react-router-dom';
import {MdArchive, MdModeEdit} from 'react-icons/md';
import {
  createTodo,
  archiveTodo,
  getAllTodos,
  updateTodo,
  indexHandler,
  getTodoById,
  errorModifier
} from '../../services/todos';
import {logoutUser} from '@app/store/reducers/auth';
import {useDispatch} from 'react-redux';
import handleStatus from '../../utils/ajax';
import {toast} from 'react-toastify';
import {Button} from 'react-bootstrap';
import Pagination from '../../components/pagination/index';
import * as yup from 'yup';
import {format} from 'date-fns';

const initials = {text: '', expiryDate: '', priority: ''};

const validationSchema = yup.object().shape({
  text: yup.string().required(),
  expiryDate: yup.date().required(),
  priority: yup.string().required()
});

const errorStyles = {
  color: 'red',
  margin: '8px 0px 8px 10px',
  fontSize: '16px'
};

const Todos = () => {
  const [mode, setMode] = useState('CREATE');
  const [todos, setTodos] = useState([]);
  const [formValues, setFormValues] = useState({});
  const navigate = useNavigate();
  const id = useParams().id;
  const dispatch = useDispatch();
  const [title, setTitle] = useState('');
  const childRef = useRef();
  const [startDate, setStartDate] = useState(0);
  const [endDate, setEndDate] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [postPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const allTodos = async (query: any) => {
    setLoading(true);
    const resp = await getAllTodos(query);
    handleStatus(resp.status, () => dispatch(logoutUser()), navigate);
    if (resp.status === 200) {
      setTotalRecords(resp.data.totalRecords);
      setTodos(resp.data.data);
      setLoading(false);
    }
  };

  const archiveHandler = async (id: any) => {
    const resp = await archiveTodo(id);
    handleStatus(resp.status, () => dispatch(logoutUser()), navigate);
    if (resp.status === 200) {
      toast.success('Archived Successfully');
      const updated = indexHandler(todos, id);
      setTodos(updated);
    }
  };

  const isValidPriorty = (data: any) => {
    let isValid =
      data.priority === 'urgent' ||
      data.priority === 'low' ||
      data.priority === 'medium';
    return isValid;
  };

  const updateHandler = async (todoId: any) => {
    navigate(`/update-todo/${todoId}`);
  };

  const handleCustomDateChange = ({startDate, endDate}) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const filter = async () => {
    setLoading(true);
    const resp = await getAllTodos({
      startDate: startDate,
      endDate: endDate,
      searchText: title
    });

    handleStatus(resp.status, () => dispatch(logoutUser()), navigate);
    if (resp.status === 200) {
      setTodos(resp.data.data);
      setLoading(false);
    }
  };

  const reset = () => {
    setTitle('');
    setStartDate(0);
    setEndDate(0);
    childRef.current.resetDatePicker();
    allTodos({});
  };

  const readableDateMaker = (date: any) => {
    const readableDate = format(date, 'dd/MM/yyyy');
    return readableDate;
  };

  const paginate = (pageno: any) => {
    allTodos({startDate, endDate, pageNo: pageno});
    setCurrentPage(pageno);
  };

  useEffect(() => {
    const getTodoData = async (id: any) => {
      const resp = await getTodoById(id);
      handleStatus(resp.status, () => dispatch(logoutUser()), navigate);
      const data = resp.data.data;
      const dateModified = format(data.expiryDate, 'yyyy-MM-dd');
      data.expiryDate = dateModified;
      setFormValues(data);
    };
    if (id) {
      getTodoData(id);
      setMode('UPDATE');
    }
  }, [id]);

  const priortyStyler = (priority: any) => {
    if (priority === 'low') {
      return (
        <span
          style={{borderRadius: '4px', width: '100px'}}
          className="text-uppercase bg-success p-2 "
        >
          {priority}
        </span>
      );
    }

    if (priority === 'medium') {
      return (
        <span
          style={{borderRadius: '4px', width: '100px'}}
          className="text-uppercase bg-warning p-2 "
        >
          {priority}
        </span>
      );
    }
    if (priority === 'urgent') {
      return (
        <span
          style={{borderRadius: '4px', width: '100px'}}
          className="text-uppercase bg-danger p-2 "
        >
          {priority}
        </span>
      );
    }
  };

  const path = localStorage.getItem('Path');
  const pageNo = localStorage.getItem('PageNo');

  useEffect(() => {
    if (path === 'todos' && pageNo !== 1) {
      setCurrentPage(pageNo);
      allTodos({pageNo: pageNo});
    } else {
      localStorage.setItem('Path', 'todos');
      localStorage.setItem('PageNo', currentPage);
      allTodos({});
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('PageNo', currentPage);
  }, [currentPage]);

  return (
    <Fragment>
      <ContentHeader title="My Todo's" path="" />
      <section className="content">
        <div className="card">
          <div
            style={{
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '5px auto',
              margin: '10px auto'
            }}
          >
            <Formik
              initialValues={formValues || initials}
              enableReinitialize={true}
              validationSchema={validationSchema}
              onSubmit={async (values) => {
                if (mode === 'CREATE') {
                  const validPriority = isValidPriorty(values);
                  if (validPriority) {
                    const create = await createTodo(values);
                    handleStatus(
                      create.status,
                      () => dispatch(logoutUser()),
                      navigate
                    );
                    if (create.status === 200) {
                      toast.success('Created Successfully.');
                      values.text = '';
                      values.expiryDate = '';
                      values.priority = '';
                      allTodos({pageNo: pageNo});
                    }
                    return;
                  }
                  toast.error('Please choose a valid priority!');
                }
                if (mode === 'UPDATE') {
                  const {_id, text, expiryDate, priority} = values;
                  const update = await updateTodo(
                    {text, expiryDate, priority},
                    _id
                  );
                  handleStatus(
                    update.status,
                    () => dispatch(logoutUser()),
                    navigate
                  );

                  if (update.status === 200) {
                    values.text = '';
                    values.expiryDate = '';
                    values.priority = '';
                    toast.success('Updated successfuly...');
                    navigate('/todos');
                    setMode('CREATE');
                    allTodos({pageNo: pageNo});
                  }
                }
              }}
            >
              {({isSubmitting, errors}) => (
                <Form
                  style={{width: '700px'}}
                  className="card-header px-1 d-flex flex-column"
                >
                  <div className="form-group row">
                    <label className="col-sm-2 col-form-label">
                      Enter Todo
                    </label>
                    <Field
                      type="text"
                      name="text"
                      className="form-control col-sm my-2 "
                      placeholder="Write your Todo...."
                    />
                    {errors.text ? (
                      <div style={errorStyles}>
                        {errorModifier(errors.text)}
                      </div>
                    ) : null}
                  </div>

                  <div className="form-group row">
                    <label className="col-sm-2 col-form-label">Due Date </label>
                    <Field
                      type="date"
                      name="expiryDate"
                      className="form-control col-sm my-2"
                      placeholder="Due Date"
                    />
                    {errors.expiryDate ? (
                      <div style={errorStyles}>
                        {errorModifier(errors.expiryDate)}
                      </div>
                    ) : null}
                  </div>

                  <div className="form-group row">
                    <label className="col-sm-2 col-form-label">Priority</label>
                    <Field
                      as="select"
                      name="priority"
                      className="form-control col-sm my-2"
                    >
                      <option value="" selected>
                        --- Select Priority ---
                      </option>
                      <option value="urgent">Urgent</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </Field>
                    {errors.priority ? (
                      <div style={errorStyles}>
                        {errorModifier(errors.priority)}
                      </div>
                    ) : null}
                  </div>

                  <div className="d-flex align-items-center justify-content-end">
                    <button
                      style={{width: '84%'}}
                      type="submit"
                      className="my-2 btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {mode}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>

          <div className="card-body">
            <div className="card-header d-flex p-2 mt-3">
              <div className="col-xl">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Serach text"
                  onChange={(e) => setTitle(e.target.value)}
                  value={title}
                />
              </div>

              <div className="col-xl ">
                <div className="form-group m-0">
                  <DatePicker
                    ref={childRef}
                    onSelect={handleCustomDateChange}
                  />
                </div>
              </div>

              <div className="col-md-1">
                <Button variant="primary" onClick={filter}>
                  Search
                </Button>
              </div>
              <div className="col-md-1 caseResetButton">
                <Button variant="primary" onClick={reset}>
                  Reset
                </Button>
              </div>
            </div>
            <div>
              <div className="card m-0">
                {loading ? (
                  <Loader title="My Todos" />
                ) : todos.length === 0 ? (
                  <h1 className="text-center" style={{padding: '5rem'}}>
                    No Task Found
                  </h1>
                ) : (
                  <table className="table table-hover text-nowrap">
                    <thead>
                      <tr
                        className="row px-2"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%'
                        }}
                      >
                        <th style={{width: '46%'}} className="text-left col-xs">
                          Content
                        </th>
                        <th
                          style={{width: '18%'}}
                          className="text-right col-xs"
                        >
                          Priority
                        </th>
                        <th
                          style={{width: '18%'}}
                          className="text-right col-xs"
                        >
                          Expiry Date
                        </th>
                        <th
                          style={{width: '18%'}}
                          className="text-right col-xs"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {todos.map((todo: any, idx) => (
                        <tr
                          key={idx}
                          className="row m-0 p-0"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%'
                          }}
                        >
                          <td className="col-xl" style={{maxWidth: '635px'}}>
                            <p
                              className="text-break"
                              style={{
                                overflowWrap: 'break-word',
                                whiteSpace: 'normal'
                              }}
                            >
                              {todo.text}
                            </p>
                          </td>

                          <td
                            style={{width: '100px'}}
                            className="text-center col-xs d-flex align-items-center justify-content-center "
                          >
                            {todo.priority
                              ? priortyStyler(todo.priority)
                              : 'N/A'}
                          </td>
                          <td
                            style={{width: '100px'}}
                            className="text-center  text-wrap col-xs d-flex align-items-center justify-content-center "
                          >
                            <span>
                              {todo.expiryDate
                                ? readableDateMaker(todo.expiryDate)
                                : 'N/A'}
                            </span>
                          </td>
                          <td
                            style={{width: '100px'}}
                            className="text-center col-xs d-flex flex-nowrap align-items-center justify-content-center"
                          >
                            <MdModeEdit
                              onClick={() => updateHandler(todo._id)}
                              title="Edit"
                              style={{
                                fontSize: '20px',
                                marginLeft: '4px',
                                cursor: 'pointer'
                              }}
                            />

                            <MdArchive
                              onClick={() => archiveHandler(`${todo._id}`)}
                              title="Archive"
                              style={{
                                fontSize: '20px',
                                marginLeft: '4px',
                                cursor: 'pointer',
                                color: 'green'
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      {todos.length === 0 ? (
        ''
      ) : (
        <Pagination
          postsPerPage={postPerPage}
          totalPosts={totalRecords}
          paginate={paginate}
          currentPage={currentPage}
        />
      )}
    </Fragment>
  );
};
export default Todos;
